import { useMemo, memo, FC, useState, useEffect, useRef, useCallback, RefObject, Fragment } from 'react'

import { PreviewTopic } from '~/types/theory'
import { batchArray } from '~/utils/helpers'
import FilePreview from '~/encyclopedia/components/FilePreview'

interface Props {
  previewTopics: PreviewTopic[]
}

function usePreviewTopics(topics: PreviewTopic[]): {
  displayedTopics: PreviewTopic[]
  nonDisplayedTopics: PreviewTopic[]
  topicsContainerRef: RefObject<HTMLDivElement>
} {
  const batchedTopics = useMemo(() => {
    return batchArray(topics, 12)
  }, [topics])

  const [index, setIndex] = useState(0)
  const [displayedTopics, setDisplayedTopics] = useState<PreviewTopic[]>(batchedTopics[0])
  const [nonDisplayedTopics, setNonDisplayedTopics] = useState<PreviewTopic[]>(() =>
    batchedTopics.slice(1).flatMap((batch) => batch),
  )
  const setDisplayed = useCallback((nextTopics: PreviewTopic[]) => {
    setDisplayedTopics((currentTopics) => [...currentTopics, ...nextTopics])
    setNonDisplayedTopics((currentTopics) =>
      currentTopics.filter((topic) => !nextTopics.some((nextTopic) => nextTopic.path === topic.path)),
    )
  }, [])
  const nextBatch = batchedTopics[index + 1]
  useEffect(() => {
    setIndex(0)
  }, [topics])

  useEffect(() => {
    setDisplayedTopics(batchedTopics[0])
    setNonDisplayedTopics(batchedTopics.slice(1).flatMap((batch) => batch))
  }, [batchedTopics])

  const topicsContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const topicsContainer = topicsContainerRef?.current
    document.addEventListener('scroll', onScroll)
    function onScroll(): void {
      const windowScrollPosition = window.scrollY
      const windowHeight = window.innerHeight
      const scrollHeight = topicsContainer?.scrollHeight as number

      const threshold = windowScrollPosition + windowHeight >= scrollHeight
      if (threshold && nextBatch?.length) {
        setIndex((prev) => prev + 1)
        setDisplayed(nextBatch)
      }
    }

    return () => document.removeEventListener('scroll', onScroll)
  }, [nextBatch, setDisplayed])

  return { displayedTopics, nonDisplayedTopics, topicsContainerRef }
}

const PreviewComponent: FC<Props> = ({ previewTopics }) => {
  const { displayedTopics, nonDisplayedTopics, topicsContainerRef } = usePreviewTopics(previewTopics)

  const renderTopicsList = (
    topics: PreviewTopic[],
    { showImage, ref }: { showImage: boolean; ref?: RefObject<HTMLDivElement> },
  ): React.ReactElement => {
    return (
      <>
        <ul className="xl:max-w-max grid grid-cols-1 xl:grid-cols-3 gap-6 mt-4 md:mt-0" ref={ref}>
          {topics.map((fileTopic) => {
            return (
              <li key={fileTopic.path}>
                <FilePreview fileTopic={fileTopic} showImage={showImage} />
              </li>
            )
          })}
        </ul>
      </>
    )
  }
  return (
    <div className="flex flex-col">
      {renderTopicsList(displayedTopics, { showImage: true, ref: topicsContainerRef })}
      {renderTopicsList(nonDisplayedTopics, { showImage: false })}
    </div>
  )
}

export default memo(PreviewComponent)
